import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';
import { Stack, IconButton, Text } from '@cad-arc';
import { CautionsTabLocation } from '@mark43/cad-api';
import { LinkText } from '../../../../../common/components/link-text';
import { createCell, SimpleTable } from '../../../../../common/components/simple-table';
import { useBroadcast } from '../../../../../common/broadcast-channel/use-broadcast';
import { TabSection } from '../../../../../panels/entity-panels/tabs/tab-section';
import { CautionPills } from '../caution-pills';
import { useViewLocationOnMap } from '../../../../../location/hooks/use-view-location-on-map';
import { useApplicationType } from '../../../../../common/hooks/use-application-type';
import {
    extractLatLngFromBeginningOfAddressString,
    isValidLatLng,
} from '../../../../../location/helpers';

export const NearbyLocationsSection = ({
    data,
    isLoading,
}: {
    data: CautionsTabLocation[] | undefined;
    isLoading?: boolean;
}) => {
    const viewLocationOnMap = useViewLocationOnMap();
    const { postMessage: legacyStoreBroadcastPostMessage } = useBroadcast('legacy-store-broadcast');
    const { disableLegacyMapAndSearches, mapWindowEnabled } = useEnabledFeatures();
    const { isFirstResponder } = useApplicationType();

    const commonContent = (
        <SimpleTable
            hasBorders={false}
            columnStyles={[
                ...(isFirstResponder ? [{ width: '5%', padding: 0 }] : []),
                { width: isFirstResponder ? '80%' : '75%' },
                { width: isFirstResponder ? '15%' : '25%' },
            ]}
            rows={[
                [
                    ...(isFirstResponder
                        ? []
                        : [
                              createCell({
                                  content: 'Location',
                              }),
                              createCell({
                                  content: 'Distance',
                              }),
                          ]),
                ],
                ...(data || []).map((nearbyCaution) => {
                    const {
                        latitude,
                        longitude,
                        nearbyLocationAddress,
                        priority1Pills,
                    } = nearbyCaution;
                    // if the nearbyLocationAddress is a `lat,lng` address string, this address must be a verified `LAT_LONG` location.
                    const preferLatLngSourceLocation = !!extractLatLngFromBeginningOfAddressString(
                        nearbyLocationAddress
                    );

                    return [
                        ...(isFirstResponder
                            ? [
                                  createCell({
                                      content: (
                                          <IconButton
                                              size="sm"
                                              variant="ghost"
                                              icon="Crosshairs"
                                              marginTop="2.5px"
                                              onClick={() => {
                                                  viewLocationOnMap({
                                                      fullAddress: preferLatLngSourceLocation
                                                          ? undefined
                                                          : nearbyLocationAddress,
                                                      latitude: latitude as number,
                                                      longitude: longitude as number,
                                                      shouldMoveMapCenterToLoggedInUnit: false,
                                                      preferLatLngSourceLocation,
                                                  });
                                              }}
                                          />
                                      ),
                                  }),
                              ]
                            : []),
                        createCell({
                            content: (
                                <Stack key="cautions" spacing={1}>
                                    <Text>{nearbyLocationAddress}</Text>
                                    <CautionPills cautions={priority1Pills} />
                                </Stack>
                            ),
                        }),
                        createCell({
                            content:
                                isValidLatLng(latitude, longitude) && !isFirstResponder ? (
                                    <LinkText
                                        key="link"
                                        data-testid="SHOW_LOCATION_ON_MAP_LINK"
                                        onClick={() => {
                                            if (disableLegacyMapAndSearches || mapWindowEnabled) {
                                                viewLocationOnMap({
                                                    // `fullAddress` for LAT_LONG location may have format `36.722272, -119.811446 City, State, USA`, location search cannot find any matched location in this format,
                                                    // send undefined `fullAddress` in this case, then `36.722272, -119.811446` will be used as query string.
                                                    fullAddress: preferLatLngSourceLocation
                                                        ? undefined
                                                        : nearbyLocationAddress,
                                                    latitude: latitude as number,
                                                    longitude: longitude as number,
                                                    preferLatLngSourceLocation,
                                                });
                                            } else {
                                                legacyStoreBroadcastPostMessage({
                                                    type: 'SHOW_LOCATION_ON_AVL_MAP',
                                                    payload: {
                                                        latitude: latitude as number,
                                                        longitude: longitude as number,
                                                        fullAddress: nearbyLocationAddress ?? '',
                                                    },
                                                });
                                            }
                                        }}
                                    >
                                        {nearbyCaution.distanceString}
                                    </LinkText>
                                ) : (
                                    <Text color="tertiary">{nearbyCaution.distanceString}</Text>
                                ),
                        }),
                    ];
                }),
            ]}
        />
    );

    if (isFirstResponder) {
        return commonContent;
    }

    return (
        <TabSection
            title={`Nearby Locations${data?.length ? ` (${data.length})` : ``}`}
            emptyText="No Nearby Cautions Found"
            loading={isLoading}
        >
            {data && data.length && commonContent}
        </TabSection>
    );
};
